import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  HomeIcon, 
  QrCodeIcon, 
  CreditCardIcon,
  ClockIcon,
  UserIcon 
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  QrCodeIcon as QrCodeIconSolid,
  CreditCardIcon as CreditCardIconSolid,
  ClockIcon as ClockIconSolid,
  UserIcon as UserIconSolid
} from '@heroicons/react/24/solid';

const BottomNav: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const [maxWidth, setMaxWidth] = useState('100%');
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // Responsive max-width calculation - for bottom nav, use 100% to be edge-to-edge
  useEffect(() => {
    const updateMaxWidth = () => {
      // Bottom nav should be full width edge-to-edge
      setMaxWidth('100%');
      setScreenWidth(window.innerWidth);
    };

    updateMaxWidth();
    window.addEventListener('resize', updateMaxWidth);
    return () => window.removeEventListener('resize', updateMaxWidth);
  }, []);

  // Enhanced hover state tracking
  const [hoveredItem, setHoveredItem] = React.useState<string | null>(null);
  
  const navItems = [
    {
      id: 'dashboard',
      label: 'Home',
      path: '/dashboard',
      icon: HomeIcon,
      iconSolid: HomeIconSolid,
      color: '#3b82f6'
    },
    {
      id: 'payment',
      label: 'Wallet',
      path: '/payment',
      icon: CreditCardIcon,
      iconSolid: CreditCardIconSolid,
      color: '#10b981'
    },
    {
      id: 'scanner',
      label: 'Scan',
      path: '/scanner',
      icon: QrCodeIcon,
      iconSolid: QrCodeIconSolid,
      isSpecial: true,
      color: '#ff6b35'
    },
    {
      id: 'transactions',
      label: 'History',
      path: '/transactions',
      icon: ClockIcon,
      iconSolid: ClockIconSolid,
      color: '#8b5cf6'
    },
    {
      id: 'profile',
      label: 'Profile',
      path: '/profile',
      icon: UserIcon,
      iconSolid: UserIconSolid,
      color: '#f59e0b'
    }
  ];

  const isActive = (path: string) => location.pathname === path;

  // Enhanced container styles
  const navContainerStyle = {
    position: 'fixed' as const,
    bottom: 0,
    left: 0,
    right: 0,
    background: 'transparent',
    padding: 0,
    zIndex: 10000 as const, // Higher than loading screen (9999)
    height: 'auto'
  };

  const navContentStyle = {
    width: '100%',
    padding: 0
  };

  // Premium glassmorphism bar
  const navBarStyle = {
    position: 'relative' as const,
    height: 'calc(72px + env(safe-area-inset-bottom))',
    paddingBottom: 'env(safe-area-inset-bottom)',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(24px) saturate(180%)',
    borderTop: '1px solid rgba(255, 255, 255, 0.3)',
    borderLeft: 'none',
    borderRight: 'none',
    borderBottom: 'none',
    borderRadius: '0',
    boxShadow: '0 -12px 40px rgba(0, 0, 0, 0.08), 0 -4px 16px rgba(0, 0, 0, 0.04)'
  };

  // Individual item containers for precise positioning
  const leftEdgeStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flex: 1,
    minWidth: 0,
    paddingRight: screenWidth < 360 ? '8px' : '12px'
  };

  const leftCenterStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1.6, // Increased flex to push away from center
    minWidth: 0,
    paddingLeft: screenWidth < 360 ? '12px' : '16px',
    paddingRight: screenWidth < 360 ? '12px' : '16px'
  };

  const rightCenterStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1.6, // Increased flex to push away from center
    minWidth: 0,
    paddingLeft: screenWidth < 360 ? '12px' : '16px',
    paddingRight: screenWidth < 360 ? '12px' : '16px'
  };

  const rightEdgeStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: 1,
    minWidth: 0,
    paddingLeft: screenWidth < 360 ? '8px' : '12px'
  };

  // Premium nav button styles - Responsive for all mobile screen sizes
  const getButtonSizes = () => {
    // Slightly larger min-widths and no hard max-width so long labels (e.g. "Withdrawal")
    // are fully covered by the gradient pill on all screen sizes.
    if (screenWidth < 360) return { minWidth: '64px', padding: '6px 10px' };
    if (screenWidth < 375) return { minWidth: '70px', padding: '7px 11px' };
    if (screenWidth < 414) return { minWidth: '76px', padding: '8px 12px' };
    return { minWidth: '80px', padding: '8px 14px' };
  };

  const buttonSizes = getButtonSizes();
  
  const navButtonStyle = (active: boolean, isSpecial: boolean = false, itemColor: string) => {
    return {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      padding: buttonSizes.padding,
      minWidth: buttonSizes.minWidth,
      // no maxWidth for buttons → can grow to fit long text like "Withdrawal"
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      color: active ? itemColor : '#64748b',
      textDecoration: 'none',
      background: active 
        ? `linear-gradient(135deg, ${itemColor}15 0%, ${itemColor}08 100%)`
        : 'transparent',
      cursor: 'pointer',
      borderRadius: '8px',
      position: 'relative' as const,
      transform: active ? 'translateY(-1px)' : 'translateY(0)',
      boxShadow: active 
        ? `0 2px 8px ${itemColor}20, 0 1px 4px ${itemColor}10`
        : 'none',
      border: active 
        ? `1px solid ${itemColor}30`
        : '1px solid transparent'
    };
  };

  // Enhanced active indicator
  const activeIndicatorStyle = (itemColor: string) => ({
    position: 'absolute' as const,
    top: '2px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '6px',
    height: '6px',
    background: `linear-gradient(135deg, ${itemColor} 0%, ${itemColor}dd 100%)`,
    borderRadius: '50%',
    boxShadow: `0 0 12px ${itemColor}60`,
    animation: 'pulse 2s ease-in-out infinite'
  });

  // Enhanced icon styles - Responsive for all mobile screen sizes
  const getIconSize = () => {
    if (screenWidth < 360) return { normal: '22px', special: '28px' }; // Very small phones
    if (screenWidth < 375) return { normal: '24px', special: '30px' }; // Small phones
    if (screenWidth < 414) return { normal: '26px', special: '32px' }; // Medium phones
    return { normal: '26px', special: '32px' }; // Large phones and tablets
  };

  const iconSizes = getIconSize();
  
  const iconStyle = (active: boolean, isSpecial: boolean = false, itemColor: string) => ({
    width: isSpecial ? iconSizes.special : iconSizes.normal,
    height: isSpecial ? iconSizes.special : iconSizes.normal,
    marginBottom: screenWidth < 360 ? '4px' : '6px',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    color: active ? itemColor : '#64748b',
    filter: active ? `drop-shadow(0 2px 8px ${itemColor}40)` : 'none',
    transform: active ? 'scale(1.1)' : 'scale(1)'
  });

  // Enhanced label styles - Responsive for all mobile screen sizes
  const getLabelSize = () => {
    if (screenWidth < 360) return { normal: '10px', special: '11px' }; // Very small phones
    if (screenWidth < 375) return { normal: '11px', special: '12px' }; // Small phones
    if (screenWidth < 414) return { normal: '12px', special: '13px' }; // Medium phones
    return { normal: '12px', special: '13px' }; // Large phones and tablets
  };

  const labelSizes = getLabelSize();
  
  const labelStyle = (active: boolean, isSpecial: boolean = false, itemColor: string) => ({
    fontSize: isSpecial ? labelSizes.special : labelSizes.normal,
    fontWeight: active ? '700' : '600',
    color: active ? itemColor : '#64748b',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    textShadow: active ? `0 1px 4px ${itemColor}30` : 'none',
    letterSpacing: active ? '0.025em' : '0.01em',
    whiteSpace: 'nowrap' as const
  });

  // Premium center FAB - Responsive for all mobile screen sizes
  const getCenterFabSize = () => {
    if (screenWidth < 360) return { size: '60px', border: '6px', icon: '26px', label: '11px', top: '-16px', bottom: '-16px' };
    if (screenWidth < 375) return { size: '64px', border: '7px', icon: '28px', label: '12px', top: '-18px', bottom: '-18px' };
    if (screenWidth < 414) return { size: '68px', border: '7px', icon: '30px', label: '12px', top: '-19px', bottom: '-19px' };
    return { size: '72px', border: '8px', icon: '32px', label: '13px', top: '-20px', bottom: '-20px' };
  };

  const centerFabSizes = getCenterFabSize();
  
  const centerFabStyle = (active: boolean) => ({
    position: 'absolute' as const,
    top: centerFabSizes.top,
    left: '50%',
    transform: active ? 'translateX(-50%) scale(1.05)' : 'translateX(-50%) scale(1)',
    width: centerFabSizes.size,
    height: centerFabSizes.size,
    borderRadius: '50%',
    border: active 
      ? `${centerFabSizes.border} solid #ffffff` 
      : `${parseInt(centerFabSizes.border) - 1}px solid #ffffff`,
    background: active
      ? 'linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ff8c42 100%)'
      : 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
    boxShadow: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
  });

  const centerIconStyle = {
    width: centerFabSizes.icon,
    height: centerFabSizes.icon,
    color: '#ffffff',
    filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))',
    transition: 'all 0.3s ease'
  };

  const centerLabelStyle = {
    position: 'absolute' as const,
    bottom: centerFabSizes.bottom,
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: centerFabSizes.label,
    fontWeight: '700',
    color: '#ff6b35',
    textShadow: '0 1px 4px rgba(0,0,0,0.15)',
    letterSpacing: '0.025em'
  };

  // Enhanced glow effect for center button
  const centerGlowStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,107,53,0.2) 0%, rgba(255,107,53,0.1) 40%, transparent 70%)',
    animation: 'centerGlow 4s ease-in-out infinite',
    pointerEvents: 'none' as const
  };

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            transform: translateX(-50%) scale(1);
            opacity: 1;
          }
          50% {
            transform: translateX(-50%) scale(1.3);
            opacity: 0.8;
          }
        }
        
        @keyframes centerGlow {
          0%, 100% {
            opacity: 0.3;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 0.6;
            transform: translate(-50%, -50%) scale(1.1);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-3px);
          }
        }
        
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .scan-button-float {
          animation: float 4s ease-in-out infinite;
        }
        
        .nav-item-hover {
          transform: translateY(-4px) scale(1.05);
        }
        
        .nav-bar-enter {
          animation: slideUp 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
      
      <div style={navContainerStyle} className="nav-bar-enter">
        <div style={navContentStyle}>
          <div style={navBarStyle}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              height: '100%', 
              paddingTop: '12px',
              paddingBottom: '12px',
              position: 'relative',
              width: '100%',
              paddingLeft: '8px',
              paddingRight: '8px'
            }}>
              {/* Home - Left Edge */}
              {(() => {
                const item = navItems[0]; // Home
                const active = isActive(item.path);
                const IconComponent = active ? item.iconSolid : item.icon;
                const isHovered = hoveredItem === item.id;
                
                return (
                  <div style={leftEdgeStyle}>
                    <button
                      onClick={() => navigate(item.path)}
                      style={navButtonStyle(active, false, item.color)}
                      className={isHovered ? 'nav-item-hover' : ''}
                      onMouseEnter={() => setHoveredItem(item.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      {active && <div style={activeIndicatorStyle(item.color)}></div>}
                      <IconComponent style={iconStyle(active, false, item.color)} />
                      <span style={labelStyle(active, false, item.color)}>
                        {item.label}
                      </span>
                    </button>
                  </div>
                );
              })()}

              {/* Withdrawal - Left Center (between Home and Scan) */}
              {(() => {
                const item = navItems[1]; // Withdrawal
                const active = isActive(item.path);
                const IconComponent = active ? item.iconSolid : item.icon;
                const isHovered = hoveredItem === item.id;
                
                return (
                  <div style={leftCenterStyle}>
                    <button
                      onClick={() => navigate(item.path)}
                      style={navButtonStyle(active, false, item.color)}
                      className={isHovered ? 'nav-item-hover' : ''}
                      onMouseEnter={() => setHoveredItem(item.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      {active && <div style={activeIndicatorStyle(item.color)}></div>}
                      <IconComponent style={iconStyle(active, false, item.color)} />
                      <span style={labelStyle(active, false, item.color)}>
                        {item.label}
                      </span>
                    </button>
                  </div>
                );
              })()}

              {/* Scan - Center (FAB button - handled separately below) */}
              <div style={{ flex: 1.4, minWidth: 0 }}></div>

              {/* History - Right Center (between Scan and Profile) */}
              {(() => {
                const item = navItems[3]; // History
                const active = isActive(item.path);
                const IconComponent = active ? item.iconSolid : item.icon;
                const isHovered = hoveredItem === item.id;
                
                return (
                  <div style={rightCenterStyle}>
                    <button
                      onClick={() => navigate(item.path)}
                      style={navButtonStyle(active, false, item.color)}
                      className={isHovered ? 'nav-item-hover' : ''}
                      onMouseEnter={() => setHoveredItem(item.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      {active && <div style={activeIndicatorStyle(item.color)}></div>}
                      <IconComponent style={iconStyle(active, false, item.color)} />
                      <span style={labelStyle(active, false, item.color)}>
                        {item.label}
                      </span>
                    </button>
                  </div>
                );
              })()}

              {/* Profile - Right Edge */}
              {(() => {
                const item = navItems[4]; // Profile
                const active = isActive(item.path);
                const IconComponent = active ? item.iconSolid : item.icon;
                const isHovered = hoveredItem === item.id;
                
                return (
                  <div style={rightEdgeStyle}>
                    <button
                      onClick={() => navigate(item.path)}
                      style={navButtonStyle(active, false, item.color)}
                      className={isHovered ? 'nav-item-hover' : ''}
                      onMouseEnter={() => setHoveredItem(item.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      {active && <div style={activeIndicatorStyle(item.color)}></div>}
                      <IconComponent style={iconStyle(active, false, item.color)} />
                      <span style={labelStyle(active, false, item.color)}>
                        {item.label}
                      </span>
                    </button>
                  </div>
                );
              })()}
            </div>

            {/* Enhanced center floating scan button */}
            {(() => {
              const scan = navItems[2];
              const active = isActive(scan.path);
              const IconComponent = scan.iconSolid;
              
              return (
                <button
                  key={scan.id}
                  onClick={() => navigate(scan.path)}
                  style={centerFabStyle(active)}
                  className={''}
                  onMouseEnter={() => setHoveredItem(scan.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  {active && <div style={centerGlowStyle} />}
                  <IconComponent style={centerIconStyle} />
                  <span style={centerLabelStyle}>Scan</span>
                </button>
              );
            })()}
          </div>
        </div>
      </div>
    </>
  );
});

export default BottomNav; 